import * as React from 'react'
import CalendarIcon from '../../../../assets/icons_new/Calendar.svg'
import CalendarColorIcon from '../../../../assets/icons_new/CalendarColor.svg'
import DeleteIcon from '../../../../assets/icons_new/DeleteIcon.svg'
import {
  DatePickerDropDownProps,
  DatePickerRangeProps,
} from '@/modules/drivers/types'
import { createPortal } from 'react-dom'
import { DatePickerRange } from '../date/DatePickerRange'

const DatePickerRangeButton: React.FC<DatePickerDropDownProps> = ({
  label,
  placeholder = 'DD-MM-RRRR',
  iconAlign = 'left',
  fullWidth = false,
  size = 'large',
  highlightSelected = false,
  clearable,
  onChange,
  initialDate,
}) => {
  const initalDate: DatePickerRangeProps = {
    startDate: null,
    endDate: null,
  }

  const [open, setOpen] = React.useState(false)
  const [selectedDate, setSelectedDate] = React.useState(
    initialDate || initalDate,
  )
  const [previewDate, setPreviewDate] = React.useState(initalDate)

  // Sync with initialDate prop changes
  React.useEffect(() => {
    if (initialDate) {
      setSelectedDate(initialDate)
    }
  }, [initialDate])

  const containerRef = React.useRef<HTMLDivElement>(null)

  const startDate = selectedDate?.startDate || previewDate?.startDate || null
  const endDate = selectedDate?.endDate || previewDate?.endDate || null

  const handleConfirm = ({ startDate, endDate }: DatePickerRangeProps) => {
    const newDateRange = { startDate: startDate, endDate: endDate }
    setSelectedDate(newDateRange)
    if (onChange) {
      onChange(newDateRange)
    }
    setOpen(false)
  }
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
  })
  const buttonRef = React.useRef(null)

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    const willOpen = !open
    if (willOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      })
    }
    setOpen(willOpen)
  }

  React.useLayoutEffect(() => {
    if (!open || !buttonRef.current || !containerRef.current) return

    const margin = 8
    const rect = buttonRef.current.getBoundingClientRect()
    const dropdownWidth = containerRef.current.offsetWidth
    const dropdownHeight = containerRef.current.offsetHeight
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = rect.left
    if (left + dropdownWidth > viewportWidth - margin) {
      left = rect.right - dropdownWidth
    }
    if (left < margin) {
      left = margin
    }

    let top = rect.bottom + 4
    if (top + dropdownHeight > viewportHeight - margin) {
      const above = rect.top - dropdownHeight - 4
      if (above > margin) {
        top = above
      }
    }

    setDropdownPosition((prev) =>
      prev.top === top && prev.left === left ? prev : { top, left },
    )
  }, [open])

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedDate(initalDate)
    setPreviewDate(initalDate)
    setOpen(false)
  }

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setPreviewDate(initalDate)
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  return (
    <div className={`tw-relative tw-z-[200] tw-inline-block`}>
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        className={`tw-relative tw-flex tw-items-center tw-gap-2 tw-rounded tw-border ${
          startDate && highlightSelected
            ? 'tw-border-primary tw-bg-[#F1F4FF]'
            : 'tw-border-neutral/20 tw-bg-transparent'
        } tw-justify-between tw-px-2 tw-text-base-content hover:tw-bg-[#F5F5F5] ${open && 'tw-border-2 tw-border-primary'} ${
          iconAlign === 'right' ? 'tw-flex-row-reverse' : 'tw-flex-row'
        } ${fullWidth ? 'tw-w-full' : ''} ${size === 'small' ? 'tw-py-1' : 'tw-py-2'}`}
      >
        <img
          src={
            startDate && highlightSelected ? CalendarColorIcon : CalendarIcon
          }
          alt="dropdown icon"
          className="tw-h-4 tw-w-4 tw-transition-transform tw-duration-200"
        />
        <div className="tw-flex tw-items-center tw-gap-2">
          <p
            className={`tw-text-sm ${startDate && highlightSelected ? 'tw-text-primary' : ''}`}
          >
            {startDate ? (
              <>
                <span className="tw-font-medium"> od </span>{' '}
                {startDate.fullDate}
                {endDate && (
                  <>
                    <span className="tw-font-medium"> do </span>
                    {endDate.fullDate}
                  </>
                )}
              </>
            ) : label ? (
              label
            ) : (
              <span className="tw-opacity-70">{placeholder}</span>
            )}
          </p>
          {clearable && startDate && (
            <img
              src={DeleteIcon}
              onClick={handleDeleteClick}
              alt="Delete Icon"
              className="tw-z-1 tw-h-4 tw-w-4 tw-transition-transform tw-duration-200"
            />
          )}
        </div>
      </button>
      {open &&
        createPortal(
          <div
            ref={containerRef}
            className="tw-fixed tw-z-[200] tw-w-max tw-max-w-[calc(100vw-1rem)] tw-overflow-x-auto tw-shadow-md"
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <DatePickerRange
              submit={handleConfirm}
              setPreview={setPreviewDate}
              selectedDate={selectedDate}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}

export default DatePickerRangeButton
